import React, { useState, createContext, useContext, useRef } from 'react';
import validator from 'validator';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { urlConfig } from '../../config/urlConfig';

import { accessTokenRequest, sendRequest } from '../global/global.service';
import { DateTimeContext } from '../dataTime/dateTime.context';

export const CreateMeetingContext = createContext();

export const CreateMeetingContextProvider = ({ children }) => {
  const history = useHistory();
  const default_time_zone = moment.tz.guess();

  const { startDate, endDate, timeZone, convertToUTC } =
    useContext(DateTimeContext);

  const titleRef = useRef(null);
  const descRef = useRef(null);
  const urlRef = useRef(null);
  //=====================================================================
  const [meeting, setMeeting] = useState(null);
  const [image, setImage] = useState({
    value: null,
    blob: null,
    temp: null,
    error: false,
  });
  const [title, setTitle] = useState({ value: '', error: false });
  const [description, setDescription] = useState({ value: '', error: false });
  const [url, setUrl] = useState({ value: '', error: false, helperText: '' });
  const [urlChecked, setUrlChecked] = useState(false);
  const [location, setLocation] = useState({ lat: '', lng: '' });
  const [suggestedlocation, setSuggestedlocation] = useState(null);
  const [urlError, setUrlError] = useState(false);
  const [urlChecker, setUrlChecker] = useState(true);
  const [loading, setLoading] = useState(false);
  const [usersInfo, setUsersInfo] = useState([]); //[{host, username, email, uuid, location}, ...]
  const [users, setUsers] = useState([]); //[username, email]
  const [error, setError] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [invitationError, setInvitationError] = useState(false);

  const getImage = () => {
    let imageUrl = localStorage.getItem('meetingImageUrl');

    // convert base64 to raw binary data held in a string
    if (imageUrl) {
      let byteString = atob(imageUrl.split(',')[1]);

      // separate out the mime component
      let mimeString = imageUrl.split(',')[0].split(':')[1].split(';')[0];

      // write the bytes of the string to an ArrayBuffer
      let ab = new ArrayBuffer(byteString.length);

      // create a view into the buffer
      let ia = new Uint8Array(ab);

      // set the bytes of the buffer to the correct values
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      // write the ArrayBuffer to a blob, and you're done
      let blob = new Blob([ab], { type: mimeString });

      let url = URL.createObjectURL(blob);

      setImage({
        blob: blob,
        value: localStorage.getItem('meetingImageUrl')
          ? localStorage.getItem('meetingImageUrl')
          : null,
        temp: url,
        error: false,
      });
    }
  };

  const resetState = () => {
    getImage();

    setUrl({
      value: localStorage.getItem('url') ? localStorage.getItem('url') : '',
      error: false,
      helperText: '',
    });
    setUrlChecked(localStorage.getItem('url') ? true : false);
    setLocation(null);
    setUrlError(false);
    setUrlChecker(true);
    setLoading(false);
    setError(null);
  };

  const setStates = (meeting) => {
    if (meeting) {
      setMeeting(meeting);
      meeting.title && setTitle({ value: meeting.title, error: false });
      setDescription({
        value: meeting.description,
        error: false,
      });

      meeting.meetingphoto &&
        meeting.meetingphoto.image &&
        setImage({
          value: meeting.meetingphoto.image,
          blob: null,
          temp: null,
          error: false,
        });
      setUrl({ value: meeting.url ? meeting.url : '', error: false });
      setUrlChecked(meeting.url ? true : false);
      setIsPublic(meeting.is_public);

      if (meeting.location && meeting.location.lat && meeting.location.lng) {
        setLocation({ lat: meeting.location.lat, lng: meeting.location.lng });
      }
    }
  };

  const isUrl = () => {
    if (url.value && !validator.isURL(url.value)) {
      setUrl({
        ...url,
        error: true,
        helperText: 'please enter valid url!',
      });
      urlRef.current.focus();
      setUrlChecker(false);
      return false;
    } else {
      setUrl({
        ...url,
        error: false,
        helperText: '',
      });
      setUrlChecker(true);
    }
  };

  const getMeeting = (id) => {
    accessTokenRequest()
      .then((response) => {
        sendRequest(
          `${urlConfig.meeting_url}${parseInt(id)}/`,
          response,
          'get',
          null
        )
          .then((res) => {
            setStates(res.data);
          })
          .catch((err) => {
            console.log('get token err', err);
          });
      })
      .catch((err) => {
        console.log('get token err', err);
      });
  };

  const invitationUsersChecker = () => {
    const result = users && users.some((user) => user.isValid === false);
    setInvitationError(result);

    return result;
  };

  const createMeeting = (e, id) => {
    e.preventDefault();
    setError(null);
    let zone = timeZone ? timeZone : default_time_zone;

    if (id) {
      if (image.value) {
        setImage({ ...image, error: null });
      } else {
        setImage({ ...image, error: 'please insert image' });
        return false;
      }
    } else {
      if (image.blob) {
        setImage({ ...image, error: null });
      } else {
        setImage({ ...image, error: 'please insert image' });
        return false;
      }
    }

    if (
      validator.isEmpty(title.value) ||
      !title.value.replace(/\s/g, '').length
    ) {
      setTitle({ ...title, error: true });
      titleRef.current.focus();
      return false;
    }

    if (
      validator.isEmpty(description.value) ||
      !description.value.replace(/\s/g, '').length
    ) {
      setDescription({ ...description, error: true });
      if (title.value !== '') {
        descRef.current.focus();
      }
      return false;
    }

    invitationUsersChecker();

    //=================================
    const meetingData = new FormData();

    image.blob && meetingData.append('photo', image.blob, 'photo.jpeg');
    meetingData.append('title', title.value);
    meetingData.append('description', description.value);
    meetingData.append('date_time', convertToUTC(startDate, zone));
    meetingData.append('end_date_time', convertToUTC(endDate, zone));
    meetingData.append('url', url.value);
    meetingData.append('time_zone', zone);
    meetingData.append('is_public', isPublic);
    meetingData.append('is_virtual', urlChecked);
    meetingData.append('with_zoom', true);

    if (users && users.length > 0) {
      const invitedUsers = users.map((user) => user.username);
      for (let i = 0; i < users.length; i++) {
        meetingData.append(
          `invited_users[${i}]`,
          invitedUsers[i].toLowerCase()
        );
      }
    } else {
    }

    if (location && location.lat && location.lng) {
      meetingData.append('location.lat', location.lat);
      meetingData.append('location.lng', location.lng);
    } else {
      meetingData.append('location.lat', '');
      meetingData.append('location.lng', '');
    }

    //=================================

    let baseUrl = '';
    let method = '';

    if (id) {
      baseUrl = `${urlConfig.meeting_url}${parseInt(id)}/`;
      method = 'put';
    } else {
      baseUrl = urlConfig.meeting_url;
      method = 'post';
    }

    if (!invitationUsersChecker()) {
      setLoading(true);
      accessTokenRequest()
        .then((response) => {
          sendRequest(baseUrl, response, method, meetingData)
            .then((res) => {
              !id && localStorage.clear();
              setLoading(false);
              history.push('/meetings');
            })
            .catch((e) => {
              setError(e.toString());
              setLoading(false);
            });
        })
        .catch((err) => {
          console.log('refresh token error: ', err.response);
          setError(err.toString());
          setLoading(false);
        });
    }
  };

  const states = {
    meeting,
    titleRef,
    descRef,
    urlRef,
    title,
    description,
    image,
    urlChecked,
    url,
    urlError,
    users,
    loading,
    error,
    location,
    suggestedlocation,
    isPublic,
    usersInfo,
    invitationError,
    setInvitationError,
    setIsPublic,
    setSuggestedlocation,
    setUrl,
    setUrlError,
    setUrlChecked,
    setImage,
    setLocation,
    setTitle,
    setDescription,
    setUsers,
    setUsersInfo,
  };

  const functions = {
    resetState,
    setStates,
    getMeeting,
    createMeeting,
  };

  return (
    <CreateMeetingContext.Provider
      value={{
        states,
        functions,
      }}
    >
      {children}
    </CreateMeetingContext.Provider>
  );
};
